import { cn } from "@/lib/utils";

interface WellnessSignalProps {
  title: string;
  subtitle: string;
  value: string | number;
  trend?: number;
  trendLabel?: string;
  icon: React.ReactNode;
  variant?: "rose" | "sage" | "lavender" | "sand";
}

const variantStyles = {
  rose: {
    container: "bg-gradient-to-br from-rose-50/80 to-pink-50/60 dark:from-rose-950/30 dark:to-pink-950/20",
    icon: "bg-rose-100/80 text-rose-600 dark:bg-rose-900/40 dark:text-rose-400",
    accent: "text-rose-600/70 dark:text-rose-400/70",
  },
  sage: {
    container: "bg-gradient-to-br from-emerald-50/80 to-teal-50/60 dark:from-emerald-950/30 dark:to-teal-950/20",
    icon: "bg-emerald-100/80 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400",
    accent: "text-emerald-600/70 dark:text-emerald-400/70",
  },
  lavender: {
    container: "bg-gradient-to-br from-violet-50/80 to-purple-50/60 dark:from-violet-950/30 dark:to-purple-950/20",
    icon: "bg-violet-100/80 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400",
    accent: "text-violet-600/70 dark:text-violet-400/70",
  },
  sand: {
    container: "bg-gradient-to-br from-amber-50/80 to-orange-50/60 dark:from-amber-950/30 dark:to-orange-950/20",
    icon: "bg-amber-100/80 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400",
    accent: "text-amber-600/70 dark:text-amber-400/70",
  },
};

export function WellnessSignal({
  title,
  subtitle,
  value,
  trend,
  trendLabel,
  icon,
  variant = "rose",
}: WellnessSignalProps) {
  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        "relative rounded-3xl p-8 transition-all duration-500 ease-out",
        "hover:scale-[1.02] hover:shadow-lg hover:shadow-primary/5",
        styles.container
      )}
    >
      {/* Icon */}
      <div className={cn(
        "inline-flex items-center justify-center rounded-2xl p-3 mb-6",
        styles.icon
      )}>
        {icon}
      </div>

      {/* Content */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-foreground/60 tracking-wide">
          {title}
        </h3>
        <p className="text-4xl font-serif font-light tracking-tight text-foreground/90">
          {value}
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {subtitle}
        </p>
      </div>

      {/* Trend - subtle indicator */}
      {trend !== undefined && (
        <div className={cn(
          "mt-6 pt-6 border-t border-foreground/5",
          "flex items-center gap-2 text-sm"
        )}>
          <span className={cn(
            "inline-flex items-center gap-1 font-medium",
            trend >= 0 ? styles.accent : "text-muted-foreground"
          )}>
            {trend >= 0 ? "+" : ""}{trend}%
          </span>
          <span className="text-muted-foreground/70">
            {trendLabel || "vs last period"}
          </span>
        </div>
      )}
    </div>
  );
}
